import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectCourseGroupAttendances,
  selectStudentListByDate,
  selectTickedAttendances,
  tryCleanStudentList,
  tryGetCourseGroupAttendances,
  tryGetStudentListByDate,
  tryTicksStudentsAttendances,
} from "../../../store/slices/teacher-courses";
import {
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Drawer,
  IconButton,
  Switch,
  useTheme,
  useMediaQuery,
  Box,
  Stack,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { NameTypography } from "../../layout/name-display";

import ModalMessages from "../../layout/modal-messages";
import CustomColumnMenu from "../../layout/grid-column-menu";
import { useFormik } from "formik";
import { DataGrid } from "@mui/x-data-grid";
import Spinner from "../../layout/spinner";
import moment from "moment";
import "moment/locale/es";
import StudentTeachersAttendancesSkeleton from "../skeleton/attendancesSkeleton";

const AttendanceDrawer = ({
  setOpenAttendanceDrawer,
  setOpenDialog,
  openAttendanceDrawer,
  courseGroupId,
  teacherCourseId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(undefined);

  const courseGroupAttendances = useSelector(selectCourseGroupAttendances);
  const tickedCourseGroupAttendances = useSelector(selectTickedAttendances);

  const [teacherAttendance, setTeacherAttendance] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (openAttendanceDrawer)
      dispatch(
        tryGetCourseGroupAttendances({ courseGroupId, teacherCourseId })
      );
  }, [openAttendanceDrawer, courseGroupId, teacherCourseId, dispatch]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceDate]);

  const fetchData = async () => {
    if (!attendanceDate) return;
    const action = await dispatch(
      tryGetStudentListByDate({
        courseGroupId,
        attendanceDate,
        teacherCourseId,
      })
    );
    if (action.payload.data.isValid) {
      setRows(
        action.payload.data.data.students.map((x) => {
          return {
            nickName: x.nickName,
            teacherValid: x.teacherValid,
            studentCourseId: x.studentCourseId,
          };
        })
      );
      setTeacherAttendance(action.payload.data.data.isValidTeacher);
      setShow(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      courseGroupId: courseGroupId,
      attendanceDate: "",
    },
  });

  const saveAttendances = async (model) => {
    const request = model.map(({ nickName, ...rest }) => {
      return rest;
    });
    const teacherPresent = teacherAttendance;

    const action = await dispatch(
      tryTicksStudentsAttendances({
        attendanceDate,
        request,
        teacherCourseId,
        teacherPresent,
      })
    );

    if (action.payload.data.isValid) {
      setOpenDialog(true);
      handleCleanStudentsOnClose();
      setOpenAttendanceDrawer(false);
    }
  };

  const initializeDataGrid = () => {
    return {
      columns: {
        columnVisibilityModel: {
          studentCourseId: false,
        },
      },
      pagination: { paginationModel: { pageSize: 6 } },
    };
  };

  const handleChangeTeacherAttendance = (value) => {
    setTeacherAttendance(value);
  };

  const handleChange = (params, value) => {
    const updatedRows = rows.map((row) =>
      row.studentCourseId === params.row.studentCourseId
        ? { ...row, teacherValid: value }
        : row
    );
    setRows(updatedRows);
  };

  const handleCleanStudentsOnClose = () => {
    dispatch(tryCleanStudentList());
    setOpenAttendanceDrawer(false);
  };

  const columns = [
    {
      field: "studentCourseId",
      headerName: "studentCourseId",
      filterable: false,
    },
    {
      field: "nickName",
      headerAlign: "left",
      align: "left",
      flex: 1,
      headerName: "Alumno",
      headerClassName: "headRow",
      minWidth: 150,
      renderCell: (params) => (
        <span
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
            padding: "8px 0",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "teacherValid",
      headerAlign: "right",
      align: "right",
      headerName: "Asistencia",
      headerClassName: "headRow",
      width: 80,
      renderCell: (params) => {
        return (
          <Switch
            checked={params.value ?? false}
            color="success"
            onChange={(_, value) => {
              handleChange(params, value);
              if (isFirstTime) {
                setTeacherAttendance(true);
                setIsFirstTime(false);
              }
            }}
          />
        );
      },
    },
  ];

  return (
    <>
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={openAttendanceDrawer}
        onClose={() => {
          handleCleanStudentsOnClose();
        }}
        PaperProps={{
          sx: {
            zIndex: 1203,
            width: isMobile ? "100vw" : 500, // Ancho original de 500 para desktop
            maxWidth: "100%",
            maxHeight: isMobile ? "90vh" : "100%",
            borderRadius: isMobile ? "12px 12px 0 0" : 0,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
        sx={{
          zIndex: 1203,
          flexShrink: 0,
        }}
      >
        {/* Header con estructura Grid original */}
        <Box sx={{ flexShrink: 0, p: 1, bgcolor: '#194B6F' }}>
          <Grid container alignItems="center">
            <Grid item xs={10}>
              <Typography
                align="left"
                sx={{
                  paddingLeft: 2,
                  paddingRight: 2,
                  paddingTop: isMobile ? 2 : 4,
                  fontWeight: 700,
                  fontSize: 18,
                  color: 'white'
                }}
              >
                Cargar Asistencia
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ textAlign: "end" }}>
              <IconButton
                sx={{ color: "white", width: 44, height: 44 }}
                onClick={() => {
                  handleCleanStudentsOnClose();
                }}
              >
                <Close />
              </IconButton>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: 2,
                paddingBottom: 2,
                display: { xs: "none", sm: "block" },
              }}
            >
              <Typography variant="body2" align="left" color="white">
                Por favor, complete información en cada campo que se solicite en el formulario
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "white",
            p: 2, // Padding general original
          }}
        >
          {/* Spacing original */}
          <Grid container rowSpacing={3} spacing={3}>
            {courseGroupAttendances.isLoading ||
              tickedCourseGroupAttendances.isLoading ? (
              <StudentTeachersAttendancesSkeleton />
            ) : (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="date-label">Fecha Clase</InputLabel>
                    <Select
                      labelId="date-label"
                      name="date"
                      label="Fecha Clase"
                      size="small"
                      value={formik.values.attendanceDate}
                      onChange={(e) => {
                        const { value } = e.target;
                        formik.setFieldValue("attendanceDate", value);
                        setAttendanceDate(value);
                      }}
                    >
                      {courseGroupAttendances?.data
                        ? courseGroupAttendances.data.map((x) => (
                          <MenuItem
                            key={x.teacherCourseAttendanceId}
                            value={x.attendanceDate}
                            sx={{ fontSize: { xs: 14, sm: "inherit" }, py: 1.5 }}
                          >
                            {
                              (moment.locale("es"),
                                moment(x.attendanceDate).format("ll h:mm a"))
                            }
                          </MenuItem>
                        ))
                        : null}
                    </Select>
                    <FormHelperText htmlFor="course"></FormHelperText>
                  </FormControl>
                </Grid>

                {show && (
                  <>
                    {/* Estructura dividida para Asistencia Profesor: Flex en Mobile, Grid 8/4 en Desktop */}
                    {isMobile ? (
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ px: 1, py: 0.5 }}
                        >
                          <Typography color="text.primary" sx={{ fontSize: 16, fontWeight: 500 }}>
                            Asistencia Profesor
                          </Typography>
                          <Switch
                            size="medium"
                            checked={teacherAttendance}
                            color="success"
                            onChange={(event) => {
                              const value = event.target.checked;
                              handleChangeTeacherAttendance(value);
                            }}
                          />
                        </Box>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography color="black" sx={{ fontSize: 18 }}>
                            Asistencia Profesor
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                          <Switch
                            size="medium"
                            checked={teacherAttendance}
                            color="success"
                            onChange={(event) => {
                              const value = event.target.checked;
                              handleChangeTeacherAttendance(value);
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  {isMobile ? (
                    <>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ px: 1, pb: 1, borderBottom: '1px solid #e0e0e0' }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.secondary' }}>
                          Alumno
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.secondary', pr: 1 }}>
                          Asistencia
                        </Typography>
                      </Box>
                      <List disablePadding>
                        {rows.map((row, index) => (
                          <React.Fragment key={row.studentCourseId}>
                            <ListItem
                              disableGutters
                              sx={{
                                py: 1,
                                px: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <NameTypography
                                color="text.primary"
                                sx={{
                                  wordBreak: "break-word",
                                  pr: 2,
                                  flex: 1,
                                  fontSize: 15
                                }}
                              >
                                {row.nickName || "Alumno sin nombre"}
                              </NameTypography>
                              <Switch
                                checked={row.teacherValid ?? false}
                                color="success"
                                onChange={(_, value) => {
                                  handleChange({ row }, value);
                                  if (isFirstTime) {
                                    setTeacherAttendance(true);
                                    setIsFirstTime(false);
                                  }
                                }}
                              />
                            </ListItem>
                            {index < rows.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </>
                  ) : (
                    <DataGrid
                      slots={{
                        columnMenu: CustomColumnMenu,
                      }}
                      autoHeight={true}
                      getRowHeight={() => "auto"}
                      rowSelection={false}
                      getRowId={(row) => row.studentCourseId}
                      rows={rows}
                      columns={columns}
                      rowsPerPageOptions={[5]}
                      initialState={initializeDataGrid()}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  {isMobile ? (
                    <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => saveAttendances(rows)}
                        disabled={tickedCourseGroupAttendances.isLoading}
                      >
                        {tickedCourseGroupAttendances.isLoading && (
                          <Spinner color="primary" />
                        )}
                        Guardar
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        disabled={tickedCourseGroupAttendances.isLoading}
                        onClick={() => {
                          handleCleanStudentsOnClose();
                        }}
                      >
                        Cancelar
                      </Button>
                    </Stack>
                  ) : (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => saveAttendances(rows)}
                        disabled={tickedCourseGroupAttendances.isLoading}
                        sx={{ width: 100, marginRight: 2 }}
                      >
                        {tickedCourseGroupAttendances.isLoading && (
                          <Spinner color="primary" />
                        )}
                        Guardar
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={tickedCourseGroupAttendances.isLoading}
                        sx={{ border: 2, width: 100 }}
                        onClick={() => {
                          handleCleanStudentsOnClose();
                        }}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {tickedCourseGroupAttendances.errors &&
          tickedCourseGroupAttendances.errors.length > 0 && (
            <ModalMessages errors={tickedCourseGroupAttendances.errors} />
          )}
      </Drawer>
    </>
  );
};

export default AttendanceDrawer;